<template>
  <div v-if="refresh">
    <McButton
      v-bind="buttonProps"
      variant="secondary"
      :text="resolvedButtonText"
      icon="icon-outline_layout"
      :disabled="loading"
      @click="showModal = !showModal"
    />

    <b-modal v-model="showModal" size="md" modal-class="modal-manage-view">
      <div class="wrapper p-2">
        <div class="section section-information">
          <p class="mc-general__label">
            {{ $t('global.object.remarks') }}
          </p>
          <div class="section-information__content p-2">
            <p class="teal-300">
              {{ $t('global.click') }} <McCheckbox v-model="alwaysChecked" />
              {{ $t('global.text.toShowColumn') }}
            </p>
            <p v-if="draggable" class="teal-300">
              {{ $t('global.click') }} <i class="icon-outline_move"></i>
              {{ $t('global.text.toMoveColumn') }}
            </p>
          </div>
        </div>
        <slot name="center-section"> </slot>
        <div v-if="hasGrouping" class="section section-group-view">
          <p class="mc-general__label">
            {{ $t('global.text.tableView') }}
          </p>
          <div class="section-group-view__content">
            <div class="section-group-view__header d-flex justify-content-start p-3">
              <div class="d-flex gap-2">
                <McCheckbox v-model="groupTable" />
                <span class="mc-general__label">{{ $t('global.text.groupByFleet') }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="section section-column-manager">
          <p class="mc-general__label">
            {{ $t('global.text.columnAdjustment') }}
          </p>
          <div class="section-column-manager__content">
            <div
              :class="`section-column-manager__header d-flex justify-content-${
                max ? 'end' : 'between'
              } p-3`"
            >
              <div v-if="!max" class="d-flex gap-2">
                <label class="mb-0"
                  ><McCheckbox
                    v-model="showAllColumn"
                    :disabled="isAnyLocked"
                    @change="handleChangeCheckAll"
                  />
                  <span class="mc-general__label ms-2">{{
                    $t('global.text.showAllColumn')
                  }}</span></label
                >
              </div>
              <button class="mc-primary" @click="handleResetColumn">
                {{ $t('global.resetAsset') }}
              </button>
            </div>
            <draggable
              v-if="draggable"
              v-model="fieldsTemp"
              class="section-column-manager__draggable mb-3"
              :move="checkHeader"
            >
              <transition-group tag="ul">
                <li
                  v-for="(field, index) in fieldsTemp"
                  :key="`${index}-${field.key}`"
                  class="section-column-manager__draggable-item"
                  :disabled="disabledCheck(field)"
                >
                  <div class="d-flex justify-content-between">
                    <div class="d-flex align-items-center gap-2">
                      <i v-if="!field.isSticky" class="icon-outline_move"></i>
                      <div v-if="!field.isSticky" class="__divider"></div>
                      <span class="d-flex gap-2 w-100">
                        <McCheckbox
                          v-model="field.shown"
                          :disabled="disabledCheck(field)"
                          @change="($event) => handleChangeParentCheckbox({ field, value: $event })"
                        />
                        {{ $t(field.label) }}
                      </span>
                    </div>
                    <div v-if="refreshAccordion" class="mc-hover-pointer">
                      <i
                        v-if="field.fields && !field.closeChild"
                        class="toggle-list icon-outline_chevron-down"
                        @click="hideChildren(field)"
                      ></i>
                      <i
                        v-if="field.fields && field.closeChild"
                        class="toggle-list icon-outline_chevron-up"
                        @click="hideChildren(field)"
                      ></i>
                    </div>
                  </div>

                  <template v-if="field.fields && !field.closeChild">
                    <hr />

                    <draggable v-model="field.fields" :move="checkHeader">
                      <transition-group tag="ul">
                        <li
                          v-for="item in field.fields"
                          :key="item.key"
                          class="section-column-manager__draggable-sub-item"
                        >
                          <div class="d-flex align-items-center gap-2">
                            <i v-if="!item.isSticky" class="icon-outline_move"></i>
                            <div v-if="!item.isSticky" class="__divider"></div>
                            <span class="d-flex gap-2 w-100">
                              <McCheckbox
                                v-model="item.shown"
                                :disabled="item.isLocked"
                                @change="
                                  ($event) => handleChangeChildCheckbox({ field, value: $event })
                                "
                              />
                              {{ $t(item.label) }}
                            </span>
                          </div>
                        </li>
                      </transition-group>
                    </draggable>
                  </template>
                </li>
              </transition-group>
            </draggable>
            <div v-else class="section-column-manager__draggable mb-3">
              <ul>
                <li
                  v-for="(field, index) in fieldsTemp"
                  :key="`${index}-${field.key}`"
                  class="section-column-manager__draggable-item section-item-no-draggable"
                >
                  <div class="d-flex justify-content-between">
                    <div class="d-flex align-items-center gap-2">
                      <div class="__divider"></div>
                      <span class="d-flex gap-2 w-100">
                        <McCheckbox
                          v-model="field.shown"
                          :disabled="disabledCheck(field)"
                          @change="($event) => handleChangeParentCheckbox({ field, value: $event })"
                        />
                        {{ $t(field.label) }}
                      </span>
                    </div>
                    <div v-if="refreshAccordion" class="mc-hover-pointer">
                      <i
                        v-if="field.fields && !field.closeChild"
                        class="toggle-list icon-outline_chevron-down"
                        @click="hideChildren(field)"
                      ></i>
                      <i
                        v-if="field.fields && field.closeChild"
                        class="toggle-list icon-outline_chevron-up"
                        @click="hideChildren(field)"
                      ></i>
                    </div>
                  </div>

                  <template v-if="field.fields && !field.closeChild">
                    <hr />

                    <ul>
                      <li
                        v-for="item in field.fields"
                        :key="item.key"
                        class="section-column-manager__draggable-sub-item"
                      >
                        <div class="d-flex align-items-center gap-2">
                          <div class="__divider"></div>
                          <span class="d-flex gap-2 w-100">
                            <McCheckbox
                              v-model="item.shown"
                              :disabled="item.isLocked"
                              @change="
                                ($event) => handleChangeChildCheckbox({ field, value: $event })
                              "
                            />
                            {{ $t(item.label) }}
                          </span>
                        </div>
                      </li>
                    </ul>
                  </template>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <template #modal-header>
        <span class="__title"> {{ $t('global.viewSetting') }} </span>
      </template>
      <template #modal-footer="{ cancel }">
        <b-row class="modal__footer mx-0">
          <b-col sm="12" md="12" class="modal__footer-btn">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.action.cancel')"
              @click="cancel"
            />
            <McButton variant="primary" :text="$t('global.action.apply')" @click="handleAccept" />
          </b-col>
        </b-row>
      </template>
    </b-modal>
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import McCheckbox from '@/components/McCheckbox'
import draggable from 'vuedraggable'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import { Helper } from '@/mixin/helper'
import {
  GET_USER_PREFERENCES,
  CREATE_USER_PREFERENCES,
  UPDATE_USER_PREFERENCES,
} from '@/services/api/userPreferences'

export default {
  components: {
    McButton,
    McCheckbox,
    draggable,
  },
  mixins: [Helper],
  props: {
    defaultColumnOrder: {
      type: Array,
      required: true,
    },
    currentColumnOrder: {
      type: Array,
      required: true,
    },
    draggable: {
      type: Boolean,
      required: false,
      default: true,
    },
    max: {
      type: Number,
      required: false,
      default: 0,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasGrouping: {
      type: Boolean,
      required: false,
      default: false,
    },
    groupTableValue: {
      type: Boolean,
      required: false,
      default: false,
    },
    saveToLocalStorage: {
      type: Boolean,
      required: false,
      default: false,
    },
    storageKey: {
      type: String,
      required: false,
      default: '',
    },
    storageScope: {
      type: String,
      required: false,
      default: 'company-user',
      validator(value) {
        return ['company-user', 'company'].includes(value)
      },
    },
    entityType: {
      type: String,
      required: false,
      default: '',
    },
    buttonProps: {
      type: Object,
      default: () => ({}),
    },
    buttonText: {
      type: String,
      default: null,
    },
  },
  emits: ['setColumn'],
  data() {
    return {
      showModal: false,
      fieldsTemp: [],
      alwaysChecked: true,
      refresh: true,
      refreshAccordion: true,
      groupTable: false,
      userPreferences: null,
    }
  },
  computed: {
    resolvedButtonText() {
      return this.buttonText !== null ? this.buttonText : this.$t('global.viewSetting')
    },
    showAllColumn: {
      get() {
        return this.fieldsTemp.every((field) => field.shown)
      },
      set(value) {
        this.handleChangeCheckAll(value)
      },
    },
    isAnyLocked() {
      return this.defaultColumnOrder.every(
        (field) =>
          field.isLocked || (field.children && field.children.every((child) => child.isLocked))
      )
    },
  },
  watch: {
    showModal(newValue) {
      if (!newValue) return

      if (this.saveToLocalStorage) {
        const savedColumns = localStorage.getItem(this.storageKey)

        if (savedColumns) {
          const parsed = JSON.parse(savedColumns)
          if (this.isSavedColumnMatchedScope(parsed)) {
            this.fieldsTemp = parsed.fields || []
            return
          }
        }
      }

      this.fieldsTemp = cloneDeep(this.currentColumnOrder)
    },
    async alwaysChecked() {
      await this.$nextTick()
      this.alwaysChecked = true
    },
    '$i18n.locale': function () {
      this.refresh = false

      setTimeout(() => {
        this.refresh = true
      }, 500)
    },
    defaultColumnOrder: {
      handler(val) {
        if (val && val.length > 0) {
          if (this.entityType && this.userPreferences) {
            this.applyPreferences()
          }
        }
      },
      deep: true,
    },
  },

  async mounted() {
    if (this.hasGrouping) {
      this.groupTable = this.groupTableValue
    }

    if (this.entityType) {
      await this.fetchUserPreferences()
    }

    if (this.saveToLocalStorage) {
      const savedColumns = localStorage.getItem(this.storageKey)

      if (savedColumns) {
        const parsed = JSON.parse(savedColumns)
        if (this.isSavedColumnMatchedScope(parsed)) {
          this.fieldsTemp = parsed.fields || []
          this.$emit('setColumn', cloneDeep(this.fieldsTemp))
        }
      }
    }
  },

  methods: {
    isSavedColumnMatchedScope(parsed) {
      if (parsed?.companyId !== this.getCompanyId()) return false
      if (this.storageScope === 'company') return true

      return parsed?.userId === this.getUserId()
    },
    async fetchUserPreferences(forceRefresh = false) {
      try {
        const res = await this.$apollo.query({
          query: GET_USER_PREFERENCES,
          fetchPolicy: forceRefresh ? 'network-only' : 'cache-first',
          variables: {
            filter: {
              entityType: this.entityType,
            },
          },
        })

        this.userPreferences = res?.data?.userPreferences || null
        if (this.userPreferences) {
          this.applyPreferences()
        }
      } catch (error) {
        console.error('Error fetching user preferences:', error)
      }
    },
    applyPreferences() {
      const userPrefs = this.userPreferences.userPreferences || []
      const companyPrefs = this.userPreferences.companyPreferences || []
      const selectedPref =
        userPrefs.length > 0 ? userPrefs[0] : companyPrefs.length > 0 ? companyPrefs[0] : null

      if (!selectedPref || !selectedPref.value || !selectedPref.value.columns) {
        return
      }

      const prefColumns = selectedPref.value.columns
      const fields = cloneDeep(this.defaultColumnOrder)
      const fieldMap = new Map(fields.map((f) => [f.key, f]))

      const sortedFields = []
      const seenKeys = new Set()

      prefColumns.forEach((prefCol) => {
        const field = fieldMap.get(prefCol.key)
        if (field) {
          sortedFields.push({
            ...field,
            shown: prefCol.shown !== undefined ? prefCol.shown : true,
          })
          seenKeys.add(prefCol.key)
        }
      })

      fields.forEach((field) => {
        if (!seenKeys.has(field.key)) {
          sortedFields.push({ ...field, shown: true })
        }
      })

      this.fieldsTemp = sortedFields
      this.$emit('setColumn', cloneDeep(this.fieldsTemp))
    },
    resetToCompanyPreferences() {
      const companyPrefs = this.userPreferences.companyPreferences || []
      const selectedPref = companyPrefs.length > 0 ? companyPrefs[0] : null

      if (!selectedPref || !selectedPref.value || !selectedPref.value.columns) {
        this.fieldsTemp = cloneDeep(this.defaultColumnOrder)
        return
      }

      const prefColumns = selectedPref.value.columns
      const fields = cloneDeep(this.defaultColumnOrder)
      const fieldMap = new Map(fields.map((f) => [f.key, f]))

      const sortedFields = []
      const seenKeys = new Set()

      prefColumns.forEach((prefCol) => {
        const field = fieldMap.get(prefCol.key)
        if (field) {
          sortedFields.push({
            ...field,
            shown: prefCol.shown !== undefined ? prefCol.shown : true,
          })
          seenKeys.add(prefCol.key)
        }
      })

      fields.forEach((field) => {
        if (!seenKeys.has(field.key)) {
          sortedFields.push({ ...field, shown: true })
        }
      })

      this.fieldsTemp = sortedFields
    },
    disabledCheck(field) {
      if (!this.max && !field.isLocked) return false

      const checked = this.fieldsTemp.filter((x) => x.shown).map((x) => x.key)
      return (
        this.max &&
        (field.isLocked || (checked.length === this.max && !checked.includes(field.key)))
      )
    },
    async handleAccept() {
      if (this.entityType) {
        const currentColumns = this.fieldsTemp.map((col) => ({
          key: col.key,
          shown: col.shown,
        }))

        const userPrefs = this.userPreferences?.userPreferences || []
        const selectedPref = userPrefs.length > 0 ? userPrefs[0] : null
        const oldColumns = selectedPref?.value?.columns || []

        if (!isEqual(currentColumns, oldColumns)) {
          await this.saveUserPreferences(currentColumns)
        }
      }

      if (this.saveToLocalStorage) {
        const dataToSave = {
          companyId: this.getCompanyId(),
          fields: this.fieldsTemp,
          savedAt: new Date().toISOString(),
          originalFields: this.defaultColumnOrder,
        }

        if (this.storageScope === 'company-user') {
          dataToSave.userId = this.getUserId()
        }

        localStorage.setItem(this.storageKey, JSON.stringify(dataToSave))
      }

      this.$emit('setColumn', cloneDeep(this.fieldsTemp))
      this.$emit('setColumnReport', cloneDeep(this.fieldsTemp))
      this.$emit('setField', this.fieldsTemp)
      this.$emit('setGroupView', this.groupTable)
      this.$emit('accept', cloneDeep(this.fieldsTemp))
      this.showModal = false
    },
    async saveUserPreferences(columns) {
      try {
        const isUpdate = this.userPreferences?.userPreferences?.length > 0
        const value = { columns }

        const input = {
          entityType: this.entityType,
          fieldType: 'TableColumn',
          label: this.entityType,
        }

        if (isUpdate) {
          input.data = {
            entityType: this.entityType,
            fieldType: 'TableColumn',
            label: this.entityType,
            value,
          }
        } else {
          input.value = value
        }

        await this.$apollo.mutate({
          mutation: isUpdate ? UPDATE_USER_PREFERENCES : CREATE_USER_PREFERENCES,
          variables: { input },
        })

        await this.fetchUserPreferences(true)
      } catch (error) {
        console.error('Error mutation user preferences:', error)
      }
    },
    handleResetColumn() {
      if (this.saveToLocalStorage) {
        const savedColumns = localStorage.getItem(this.storageKey)

        if (savedColumns) {
          const parsed = JSON.parse(savedColumns)

          if (this.isSavedColumnMatchedScope(parsed)) {
            this.fieldsTemp = parsed.originalFields || []
            return
          }
        }
      } else if (this.entityType && this.userPreferences) {
        this.resetToCompanyPreferences()
      } else {
        this.fieldsTemp = cloneDeep(this.defaultColumnOrder)
      }
      this.fieldsTemp = this.fieldsTemp.sort((a, b) => a.order - b.order)
    },
    handleChangeCheckAll(value) {
      this.fieldsTemp = this.fieldsTemp.map((field) => {
        if (field.fields) {
          field.fields = field.fields.map((subField) => ({
            ...subField,
            shown: value,
          }))
        }

        return {
          ...field,
          shown: field.isLocked ? true : value,
        }
      })
      this.$emit('handleSelectAllFields', value)
    },
    checkHeader(value) {
      let indexSticky = []

      if (!value.draggedContext.element.hasOwnProperty('parent')) {
        this.fieldsTemp.forEach((field, index) => {
          if (field.isSticky) indexSticky.push(index)
        })
      }

      if (
        value.draggedContext.element.isSticky ||
        indexSticky.includes(value.draggedContext.futureIndex)
      )
        return false

      return true
    },

    handleChangeParentCheckbox({ field, value }) {
      if (!field.fields) return

      field.fields.forEach((item) => {
        item.shown = value
      })
    },
    handleChangeChildCheckbox({ field, value }) {
      if (field.shown == value) return

      let totalCount = 0

      field.fields.forEach((item) => {
        if (item.shown !== value) return

        totalCount++
      })

      if (value === false && totalCount !== field.fields.length) return

      field.shown = value
    },

    hideChildren(field) {
      if (field.closeChild == undefined) field.closeChild = false

      this.refreshAccordion = false

      setTimeout(() => {
        field.closeChild = !field.closeChild
        this.refreshAccordion = true
      }, 50)
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.modal-manage-view) {
  .modal-dialog {
    border-radius: 0px;
    max-height: 100vh;
    height: 100%;
    margin: 0;
    margin-left: auto;
  }

  .modal-content {
    height: 100%;
  }

  .modal-content,
  .modal-header,
  .modal-footer {
    border-radius: 0;

    .modal__footer-btn {
      display: flex;
    }
  }

  .modal-header {
    color: $white;
    justify-content: space-between;

    .__reset {
      font-size: 13px;
      text-align: right;
      cursor: pointer;
    }
  }

  .modal-body {
    overflow: hidden;
  }

  .wrapper {
    overflow-y: hidden;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}

.section {
  :deep(input[type='checkbox']) {
    margin-bottom: 2px;
  }

  & > p {
    color: $teal-300;
    font-weight: 600;
  }

  &-information {
    &__content {
      border: 2px solid $blue-100;
      border-radius: 4px;
      margin-bottom: 20px;

      & > p {
        display: flex;
        gap: 5px;
        align-items: center;
        margin-bottom: 0;

        &:first-child {
          margin-bottom: 4px;
        }
      }
    }
  }

  &-group-view {
    flex: 1 1 auto;

    &__content {
      box-shadow: 0px 5px 15px 0px rgba(13, 68, 145, 0.08);
      overflow-y: hidden;
      margin-bottom: 20px;
    }

    &__header {
      button {
        background: transparent;
        border: none;
        color: $cyan;
        font-weight: 600;
      }
    }
  }

  &-column-manager {
    overflow: hidden;
    flex: 1 1 auto;

    &__content {
      box-shadow: 0px 5px 15px 0px rgba(13, 68, 145, 0.08);
      overflow-y: hidden;
      height: 100%;
    }

    &__header {
      button {
        background: transparent;
        border: none;
        color: $cyan;
        font-weight: 600;
      }
    }

    &__draggable {
      background: $gray;
      padding: 16px;
      overflow-y: auto;
      height: 100%;
      padding-bottom: 86px;

      ul {
        padding: 0;
      }

      &-item,
      &-sub-item {
        list-style-type: none;
        cursor: move;
        background: white;
        margin-bottom: 16px;
        border-radius: 5px;
        padding: 14px 12px;
        transition: 0.3s;

        .__divider {
          background-color: $teal-100;
          height: 16px;
          width: 1px;
        }

        .toggle-list {
          font-size: 20px;

          &::before {
            color: #3d69a7;
          }
        }
      }

      &-sub-item {
        list-style-type: none;
        border: 1px solid #cfdae9;
      }
    }
  }
}

.icon-outline_move {
  color: $blue-400;
  font-size: 16px;
}

.mc-hover-pointer {
  &:hover {
    cursor: pointer !important;
  }
}

.section-item-no-draggable {
  cursor: auto;
}
</style>
